import {
	ChangeEvent,
	FunctionComponent,
	UIEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { faFilter, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";
import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";

import { Icon } from "@/components/Icon/Icon";
import { SearchField } from "@/components/SearchField/SearchField";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";
import { List } from "@/pages/Private/pages/List/schema/list";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { ListScopeFilterRow } from "./ListScopeFilterRow";
import { buildListScopeCrossLink, ListScope } from "./listScope";
import { partitionLists, ScopeListRow, setsEqual } from "./utils";

import type { CustomListsAutocompleteForSequencerItem } from "@/pages/Private/pages/List/components/CustomListsAutocompleteForSequencer";

const PANEL_WIDTH = 400;
const VIEWPORT_MARGIN = 8;
const SCROLL_THRESHOLD = 48;

export interface ListScopeFilterProps {
	/** The messaging surface being filtered — picks the relevance copy and the cross-link target. */
	scope: ListScope;
	/** Full lists already loaded by the page (paginated + searched server-side). */
	lists: List[];
	/** The applied selection held by the page. */
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	/** Commit the staged selection — fired only on Apply. */
	onApply: (value: CustomListsAutocompleteForSequencerItem[]) => void;
	handleSearch: (value: string) => void;
	handleFetchMore: () => void;
	isFetching: boolean;
	isListLoading?: boolean;
	currentPage: number;
	totalPages: number;
	testId?: string;
	className?: string;
}

interface PanelPosition {
	top: number;
	left: number;
}

/** Cross-link to the surface a list's leads actually live on, labelled for the current scope. */
const buildCrossLink = (
	scope: ListScope,
	lists: CustomListsAutocompleteForSequencerItem[],
	t: (key: string) => string
): { href: string; label: string } | null => {
	const href = buildListScopeCrossLink(scope, lists);

	return href ? { href, label: t(`scopeFilter.irrelevant.${scope}.crossLink`) } : null;
};

export const ListScopeFilter: FunctionComponent<ListScopeFilterProps> = ({
	scope,
	lists,
	selectedLists,
	onApply,
	handleSearch,
	handleFetchMore,
	isFetching,
	isListLoading,
	currentPage,
	totalPages,
	testId,
	className,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "list" });

	const [isOpen, setIsOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [archivedExpanded, setArchivedExpanded] = useState(false);
	// `null` means "follow the default" — an explicit boolean is the user's own toggle.
	const [irrelevantExpanded, setIrrelevantExpanded] = useState<boolean | null>(null);
	const [position, setPosition] = useState<PanelPosition>({ top: 0, left: 0 });

	// Staged selection: id -> item. Keys form the staged set; values keep titles
	// so a commit survives rows scrolled out of view by search/pagination.
	const [staged, setStaged] = useState<Map<number, CustomListsAutocompleteForSequencerItem>>(
		new Map()
	);

	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const appliedSet = useMemo(
		() => new Set(selectedLists.map((item) => Number(item.id))),
		[selectedLists]
	);
	const stagedSet = useMemo(() => new Set(staged.keys()), [staged]);
	const isDirty = useMemo(() => !setsEqual(stagedSet, appliedSet), [stagedSet, appliedSet]);

	const { active, irrelevant, archived } = useMemo(() => partitionLists(lists), [lists]);

	// When nothing is relevant — the whole point of PREC-3834 — the explanatory section is the only
	// content there is, so keeping it collapsed would leave the user staring at an empty panel again.
	// Derived on every render so a search that narrows the result to irrelevant lists opens it too.
	const showIrrelevant = irrelevantExpanded ?? active.length === 0;

	const buildStagedFromApplied = useCallback(
		() => new Map(selectedLists.map((item) => [Number(item.id), item])),
		[selectedLists]
	);

	const computePosition = useCallback(() => {
		const rect = triggerRef.current?.getBoundingClientRect();

		if (!rect) {
			return;
		}

		const maxLeft = window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN;

		setPosition({
			top: rect.bottom + 6,
			left: Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft)),
		});
	}, []);

	const openDropdown = () => {
		setStaged(buildStagedFromApplied());
		setArchivedExpanded(false);
		setIrrelevantExpanded(null);
		computePosition();
		setIsOpen(true);
	};

	const closeDropdown = () => {
		setIsOpen(false);
		setSearchValue("");
	};

	// Reposition while open; close on outside click / Escape.
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const reposition = () => computePosition();

		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (target instanceof Node && target instanceof HTMLElement && target.id === "closeIcon") {
				return;
			}

			if (
				target instanceof Node &&
				!panelRef.current?.contains(target) &&
				!triggerRef.current?.contains(target)
			) {
				closeDropdown();
			}
		};

		const handleKeydown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				closeDropdown();
			}
		};

		window.addEventListener("scroll", reposition, true);
		window.addEventListener("resize", reposition);
		window.addEventListener("click", handleClick);
		window.addEventListener("keydown", handleKeydown);

		return () => {
			window.removeEventListener("scroll", reposition, true);
			window.removeEventListener("resize", reposition);
			window.removeEventListener("click", handleClick);
			window.removeEventListener("keydown", handleKeydown);
		};
	}, [isOpen, computePosition]);

	const toggleRow = (id: number, title: string) => {
		setStaged((prev) => {
			const next = new Map(prev);

			if (next.has(id)) {
				next.delete(id);
			} else {
				next.set(id, { id, title });
			}

			return next;
		});
	};

	// Clear empties the staged selection and immediately commits it, so the list
	// filter is actually reset in one click. The panel stays open so the cleared
	// state is visible and the user can reselect.
	const handleClearAll = () => {
		setStaged(new Map());
		onApply([]);
	};

	const handleApply = () => {
		onApply(Array.from(staged.values()));
		closeDropdown();
	};

	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
		handleSearch(event.target.value);
	};

	const handleSearchReset = () => {
		setSearchValue("");
		handleSearch("");
	};

	const handleBodyScroll = (event: UIEvent<HTMLDivElement>) => {
		const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

		if (
			!isFetching &&
			currentPage < totalPages &&
			scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD
		) {
			handleFetchMore();
		}
	};

	// A single selected list shows its name (the dropdown grows to fit it); multiple
	// collapse to a count, and an empty selection shows the generic prompt.
	const triggerLabel =
		selectedLists.length === 1
			? selectedLists[0].title
			: selectedLists.length > 1
				? t("scopeFilter.triggerCount", { count: selectedLists.length })
				: t("scopeFilter.trigger");

	const renderSummary = () => {
		const count = stagedSet.size;

		return (
			<div className="flex items-center justify-between text-xs font-medium">
				{count > 0 ? (
					<span className={isDirty ? "text-brand-light" : "text-gray-900"}>
						<b className="font-semibold">{count}</b>{" "}
						{t(count === 1 ? "scopeFilter.list" : "scopeFilter.lists")}{" "}
						{t("scopeFilter.selectedSuffix")}
						{isDirty ? ` · ${t("scopeFilter.unsaved")}` : ""}
					</span>
				) : (
					<span className="text-text-meta">{t("scopeFilter.noListsSelected")}</span>
				)}

				{count > 0 && (
					<button
						className="text-text-meta px-1.5 py-1 rounded-md hover:bg-gray-100 hover:text-gray-900 duration-200"
						type="button"
						onClick={handleClearAll}
					>
						{t("scopeFilter.clear")}
					</button>
				)}
			</div>
		);
	};

	const renderRow = (row: ScopeListRow, section: "active" | "irrelevant" | "archived") => (
		<ListScopeFilterRow
			key={row.id}
			crossLink={
				section === "active" ? null : buildCrossLink(scope, [{ id: row.id, title: row.title }], t)
			}
			isArchivedSection={section === "archived"}
			isIrrelevantSection={section === "irrelevant"}
			isSelected={stagedSet.has(row.id)}
			row={row}
			onToggle={() => toggleRow(row.id, row.title)}
		/>
	);

	// Nothing loaded at all. A search that matched nothing is a different situation from "you have
	// lists, they just have nothing on this surface" — the latter is what confused the customer in
	// PREC-3834, so it gets its own copy plus a way over to the surface holding those leads.
	const renderEmptyState = () => {
		if (searchValue) {
			return (
				<p className="py-6 px-3 text-sm italic text-gray-700 text-center">
					{t("scopeFilter.noResults")}
				</p>
			);
		}

		const crossLink = buildCrossLink(scope, selectedLists, t);

		return (
			<div className="flex flex-col items-center gap-2 py-6 px-4 text-center">
				<p className="text-sm font-medium text-gray-900">
					{t(`scopeFilter.irrelevant.${scope}.emptyTitle`)}
				</p>
				<p className="text-xs text-text-meta">{t(`scopeFilter.irrelevant.${scope}.hint`)}</p>
				{crossLink && (
					<a
						className="text-xs font-medium text-brand-light cursor-pointer hover:underline"
						data-test="list-scope-empty-cross-link"
						href={crossLink.href}
					>
						{crossLink.label}
					</a>
				)}
			</div>
		);
	};

	const renderBody = () => {
		if (!active.length && !irrelevant.length && !archived.length) {
			return renderEmptyState();
		}

		return (
			<>
				{active.map((row) => renderRow(row, "active"))}

				{irrelevant.length > 0 && (
					<>
						<button
							className="flex items-center justify-between w-full px-3 py-2 mt-1 border-t border-border bg-section text-xs font-medium text-text-meta cursor-pointer hover:bg-gray-100 duration-200"
							data-test="list-scope-irrelevant-toggle"
							type="button"
							onClick={() => setIrrelevantExpanded(!showIrrelevant)}
						>
							<span className="flex flex-col items-start gap-0.5 text-left">
								<span>
									{t(`scopeFilter.irrelevant.${scope}.section`, { count: irrelevant.length })}
								</span>
								<span className="font-normal text-[11px]">
									{t(`scopeFilter.irrelevant.${scope}.hint`)}
								</span>
							</span>
							{showIrrelevant ? (
								<ChevronUp className="w-3.5 h-3.5 flex-none" />
							) : (
								<ChevronDown className="w-3.5 h-3.5 flex-none" />
							)}
						</button>

						{showIrrelevant && irrelevant.map((row) => renderRow(row, "irrelevant"))}
					</>
				)}

				{archived.length > 0 && (
					<>
						<button
							className="flex items-center justify-between w-full px-3 py-2 mt-1 border-t border-border bg-section text-xs font-medium text-text-meta hover:bg-gray-100 duration-200"
							type="button"
							onClick={() => setArchivedExpanded((prev) => !prev)}
						>
							<span>{t("scopeFilter.archived", { count: archived.length })}</span>
							{archivedExpanded ? (
								<ChevronUp className="w-3.5 h-3.5" />
							) : (
								<ChevronDown className="w-3.5 h-3.5" />
							)}
						</button>

						{archivedExpanded && archived.map((row) => renderRow(row, "archived"))}
					</>
				)}

				{isFetching && currentPage < totalPages && (
					<div className="flex items-center justify-center py-4">
						<Spinner color="#4d66f5" />
					</div>
				)}
			</>
		);
	};

	return (
		<div className={classNames("relative", className)}>
			<button
				ref={triggerRef}
				className="flex items-center justify-between gap-2 w-full h-[32px] px-3 border border-gray-200 rounded-[10px] text-xs font-semibold text-gray-900 hover:border-gray-300 duration-200"
				data-test={testId}
				type="button"
				onClick={() => (isOpen ? closeDropdown() : openDropdown())}
			>
				<span className="flex items-center gap-2 min-w-0 pointer-events-none">
					<Icon className="w-3.5 h-3.5 text-gray-500" icon={faFilter} />
					<span className="truncate max-w-[320px]">{triggerLabel}</span>
				</span>
				{/* A single chevron that rotates — swapping two nodes would detach the
				    clicked icon mid-toggle and trip the outside-click handler into closing.
				    pointer-events-none keeps the button itself the sole, stable click target. */}
				<ChevronDown
					className={classNames(
						"w-4 h-4 text-gray-500 flex-none pointer-events-none duration-200",
						isOpen ? "rotate-180" : "rotate-0"
					)}
				/>
			</button>

			{isOpen &&
				createPortal(
					<div
						ref={panelRef}
						className="fixed z-[200] flex flex-col bg-white border border-gray-200 rounded-xl shadow-all overflow-hidden"
						style={{ top: position.top, left: position.left, width: PANEL_WIDTH, maxHeight: 540 }}
					>
						<div className="flex flex-col gap-2 p-3 border-b border-border">
							<SearchField
								className="!top-1/2 !-translate-y-1/2"
								classNameInput="h-[32px] !pl-10"
								handleChange={handleSearchChange}
								handleReset={handleSearchReset}
								icon={
									isListLoading || isFetching ? (
										<Spinner color="#4d66f5" />
									) : (
										<Icon icon={faSearch} />
									)
								}
								iconPosition="left"
								name="list-scope-search"
								placeholder={t("scopeFilter.searchPlaceholder")}
								value={searchValue}
							/>
							{renderSummary()}
						</div>

						<div className="flex-1 overflow-y-auto bb-scrollbar-darker" onScroll={handleBodyScroll}>
							{renderBody()}
						</div>

						<div className="flex items-center justify-end gap-1.5 p-3 border-t border-border bg-section">
							<button
								className="text-xs font-medium text-text-meta px-3 py-1.5 rounded-md hover:bg-gray-100 hover:text-gray-900 duration-200"
								type="button"
								onClick={closeDropdown}
							>
								{t("scopeFilter.cancel")}
							</button>
							<button
								className="text-xs font-medium text-white bg-blue-action px-3 py-1.5 rounded-md duration-200 disabled:bg-gray-200 disabled:text-text-meta disabled:cursor-not-allowed enabled:hover:bg-brand-light"
								disabled={!isDirty}
								type="button"
								onClick={handleApply}
							>
								{stagedSet.size > 0
									? t("scopeFilter.applyCount", { count: stagedSet.size })
									: t("scopeFilter.apply")}
							</button>
						</div>
					</div>,
					document.body
				)}
		</div>
	);
};
